/**
 * Offline quotation service — IndexedDB-backed creation, listing, and sync
 * enqueueing for quotations created while the POS terminal is offline.
 *
 * Mirrors offline-billing.service.ts in shape and conventions:
 *   - All currency in paise (BIGINT-equivalent).
 *   - GST math uses Math.floor() per line item.
 *   - Local sequence counter for offline quote numbers.
 *   - Idempotent enqueue via client_id (FR-SYNC-006).
 *
 * Note on totals computation: the helper here is intentionally duplicated
 * from offline-billing.service.ts. The two services have very similar
 * arithmetic but different output shapes (bills carry per-line discount;
 * quotes don't yet) and extracting a shared helper today would be premature
 * abstraction for ~30 lines of code. TODO: revisit when a third caller needs
 * the same math, or when bill-level discount migrates into the calc layer.
 *
 * Traceability: FR-BILL-033, FR-BILL-034, FR-SYNC-006, FR-SYNC-012.
 * PAGES.md §5.4 (Offline Behavior — quotations).
 */

import {
  posDb,
  deviceId,
  type LocalQuote,
  type LocalQuoteItem,
  type LocalQuoteStatus,
} from '@/db/pos-database';
import {
  encryptForStorage,
  decryptFromStorage,
  decryptManyFromStorage,
} from '@/db/encrypted-access';
import { enqueue } from '@/services/sync-queue.service';
import { gstWaived } from '@/lib/gst';
import type { CartLineItem } from '@/stores/use-cart-store';
import type { Quote, QuoteItem } from '@/services/quote.service';

// ─── Types ──────────────────────────────────────────────────────────────────

interface OfflineQuotePayload {
  client_id: string;
  tenant_id: string;
  location_id: string;
  quote_number: string;
  status: LocalQuoteStatus;
  customer_id: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  items: Array<{
    product_id: string;
    name: string;
    sku: string;
    unit: string;
    quantity: number;
    unit_price: number;
    unit_cost: number;
    gst_rate: number;
    hsn_code: string | null;
    discount: number;
  }>;
  subtotal: number;
  tax_total: number;
  discount_total: number;
  grand_total: number;
  is_inter_state: boolean;
  valid_until: string;
  notes: string | null;
  created_at: string;
}

export interface CreateOfflineQuoteParams {
  items: CartLineItem[];
  tenantId: string;
  locationId: string;
  locationCode: string;
  customerId: string | null;
  customerName: string | null;
  customerPhone: string | null;
  isInterState: boolean;
  validityDays?: number;
  notes?: string | null;
}

export interface ListLocalQuoteFilters {
  tenantId: string;
  locationId: string;
  status?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  /** When true, include quotes already synced to the server (default: only unsynced). */
  includeSynced?: boolean;
}

/**
 * Quote shape returned by the offline service. Carries an `is_local` marker
 * so the UI can render a "Local" badge and disable server-only actions.
 */
export interface LocalQuoteListItem extends Quote {
  is_local: true;
  client_id: string;
}

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ─── Local sequence counter ─────────────────────────────────────────────────

const LOCAL_SEQ_KEY = 'bbpos_offline_quote_seq';

function getNextLocalSequence(): number {
  const current = parseInt(localStorage.getItem(LOCAL_SEQ_KEY) ?? '0', 10);
  const next = current + 1;
  localStorage.setItem(LOCAL_SEQ_KEY, String(next));
  return next;
}

function generateOfflineQuoteNumber(locationCode: string): string {
  const now = new Date();
  const dateStr =
    String(now.getFullYear()) +
    String(now.getMonth() + 1).padStart(2, '0') +
    String(now.getDate()).padStart(2, '0');
  const seq = getNextLocalSequence();
  return `${locationCode}-Q-${dateStr}-OFL${String(seq).padStart(4, '0')}`;
}

// ─── Compute quote totals client-side (paise, Math.floor per line) ──────────

interface QuoteTotals {
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  grandTotal: number;
  itemTotals: Array<{
    line_subtotal: number;
    line_tax: number;
    line_total: number;
    discount: number;
  }>;
}

function computeQuoteTotals(items: CartLineItem[], isInterState: boolean): QuoteTotals {
  let subtotal = 0;
  let discountTotal = 0;
  let taxTotal = 0;
  const itemTotals: QuoteTotals['itemTotals'] = [];

  for (const item of items) {
    const lineSubtotal = item.unitPrice * item.quantity;

    let discountAmount = 0;
    if (item.discountType === 'PERCENTAGE') {
      discountAmount = Math.floor((lineSubtotal * item.discountValue) / 100);
    } else if (item.discountType === 'FLAT') {
      discountAmount = Math.min(item.discountValue, lineSubtotal);
    }

    const taxable = lineSubtotal - discountAmount;
    let lineTax = 0;
    if (gstWaived()) {
      // Not GST registered (gst-parked 090, D5): the quote carries no GST.
      lineTax = 0;
    } else if (isInterState) {
      lineTax = Math.floor((taxable * item.gstRate) / 100);
    } else {
      const halfRate = item.gstRate / 2;
      const cgst = Math.floor((taxable * halfRate) / 100);
      const sgst = Math.floor((taxable * halfRate) / 100);
      lineTax = cgst + sgst;
    }

    const lineTotal = taxable + lineTax;

    subtotal += lineSubtotal;
    discountTotal += discountAmount;
    taxTotal += lineTax;
    itemTotals.push({
      line_subtotal: lineSubtotal,
      line_tax: lineTax,
      line_total: lineTotal,
      discount: discountAmount,
    });
  }

  const rawTotal = subtotal - discountTotal + taxTotal;
  const grandTotal = Math.round(rawTotal / 100) * 100; // round to nearest rupee

  return { subtotal, discountTotal, taxTotal, grandTotal, itemTotals };
}

// ─── Filtering helpers ──────────────────────────────────────────────────────

function applyFilters(quotes: LocalQuote[], filters: ListLocalQuoteFilters): LocalQuote[] {
  let result = quotes;

  if (!filters.includeSynced) {
    result = result.filter((q) => q.is_synced === 0);
  }

  if (filters.status) {
    result = result.filter((q) => q.status === filters.status);
  }

  if (filters.search) {
    const needle = filters.search.toLowerCase();
    result = result.filter(
      (q) =>
        q.quote_number.toLowerCase().includes(needle) ||
        (q.customer_name?.toLowerCase().includes(needle) ?? false) ||
        (q.customer_phone?.includes(filters.search ?? '') ?? false),
    );
  }

  if (filters.startDate) {
    result = result.filter((q) => q.created_at >= filters.startDate!);
  }
  if (filters.endDate) {
    // Inclusive end-of-day comparison: append end-of-day suffix if a bare date.
    const end = filters.endDate.length === 10
      ? `${filters.endDate}T23:59:59.999Z`
      : filters.endDate;
    result = result.filter((q) => q.created_at <= end);
  }

  // Newest first
  result.sort((a, b) => b.created_at.localeCompare(a.created_at));
  return result;
}

function toListItem(local: LocalQuote): LocalQuoteListItem {
  const items: QuoteItem[] = local.items.map((it) => ({
    id: it.id,
    product_id: it.product_id,
    product_name: it.product_name,
    sku: it.sku,
    quantity: it.quantity,
    unit_price: it.unit_price,
    discount: it.discount,
    gst_rate: it.gst_rate,
    line_total: it.line_total,
    // Stock data unknown offline — show as IN_STOCK to avoid red flags on
    // quotes the cashier just created moments ago. Server values overwrite
    // on sync.
    stock_quantity: 0,
    availability: 'IN_STOCK',
  }));

  return {
    is_local: true,
    client_id: local.client_id,
    // G2.19: Prefer server_id once the quote has been synced. This lets the
    // QuotationListPage merge logic dedupe a synced-but-not-yet-purged local
    // row against the same row fetched from the server (both sides carry
    // the same id). Unsynced rows fall back to client_id, which the detail
    // page resolves via getLocalQuote(client_id) — primary key lookup.
    id: local.server_id ?? local.client_id,
    quote_number: local.quote_number,
    status: local.status,
    location_id: local.location_id,
    customer_id: local.customer_id,
    customer_name: local.customer_name,
    customer_phone: local.customer_phone,
    items,
    subtotal: local.subtotal,
    tax_total: local.tax_total,
    discount_total: local.discount_total,
    grand_total: local.grand_total,
    valid_until: local.valid_until,
    notes: local.notes,
    created_by: deviceId, // best-effort attribution offline
    converted_bill_id: null,
    created_at: local.created_at,
    updated_at: local.updated_at,
  };
}

// ─── Service ────────────────────────────────────────────────────────────────

export const offlineQuoteService = {
  /**
   * Create a quote offline: persist a LocalQuote row, then enqueue a
   * QUOTATION/CREATE entry for the sync worker. Returns the client_id.
   */
  async createQuoteOffline(params: CreateOfflineQuoteParams): Promise<string> {
    const clientId = crypto.randomUUID();
    const now = new Date().toISOString();
    const quoteNumber = generateOfflineQuoteNumber(params.locationCode);

    const totals = computeQuoteTotals(params.items, params.isInterState);

    const validityDays = params.validityDays ?? 3;
    const validUntil = new Date(Date.now() + validityDays * 86_400_000).toISOString();

    const items: LocalQuoteItem[] = params.items.map((item, idx) => ({
      id: item.id,
      product_id: item.productId,
      product_name: item.name,
      sku: item.sku,
      unit: item.unit,
      quantity: item.quantity,
      unit_price: item.unitPrice,
      unit_cost: item.unitCost,
      gst_rate: item.gstRate,
      hsn_code: item.hsnCode ?? null,
      discount: totals.itemTotals[idx].discount,
      line_subtotal: totals.itemTotals[idx].line_subtotal,
      line_tax: totals.itemTotals[idx].line_tax,
      line_total: totals.itemTotals[idx].line_total,
    }));

    const localQuote: LocalQuote = {
      client_id: clientId,
      server_id: null,
      tenant_id: params.tenantId,
      location_id: params.locationId,
      quote_number: quoteNumber,
      status: 'ACTIVE',
      customer_id: params.customerId,
      customer_name: params.customerName,
      customer_phone: params.customerPhone,
      items,
      subtotal: totals.subtotal,
      tax_total: totals.taxTotal,
      discount_total: totals.discountTotal,
      grand_total: totals.grandTotal,
      is_inter_state: params.isInterState,
      valid_until: validUntil,
      notes: params.notes ?? null,
      is_synced: 0,
      created_at: now,
      updated_at: now,
    };

    // Persist locally first so the UI can render immediately.
    const encrypted = await encryptForStorage(localQuote, 'local_quotes');
    await posDb.local_quotes.put(encrypted);

    // Enqueue for sync. G2.17 wires the backend QUOTATION case so synced
    // entries now resolve to SYNCED instead of CONFLICT.
    //
    // Invariant: every local_quotes row must have a sync-queue entry. If
    // enqueue fails after the put() (IndexedDB quota / pending_sync table
    // corruption), we roll back the local row so the cashier sees the
    // error immediately and can retry — rather than the quote silently
    // sitting in their local list forever.
    const payload: OfflineQuotePayload = {
      client_id: clientId,
      tenant_id: params.tenantId,
      location_id: params.locationId,
      quote_number: quoteNumber,
      status: 'ACTIVE',
      customer_id: params.customerId,
      customer_name: params.customerName,
      customer_phone: params.customerPhone,
      items: items.map((it) => ({
        product_id: it.product_id,
        name: it.product_name,
        sku: it.sku,
        unit: it.unit,
        quantity: it.quantity,
        unit_price: it.unit_price,
        unit_cost: it.unit_cost,
        gst_rate: it.gst_rate,
        hsn_code: it.hsn_code,
        discount: it.discount,
      })),
      subtotal: totals.subtotal,
      tax_total: totals.taxTotal,
      discount_total: totals.discountTotal,
      grand_total: totals.grandTotal,
      is_inter_state: params.isInterState,
      valid_until: validUntil,
      notes: params.notes ?? null,
      created_at: now,
    };

    try {
      await enqueue({
        client_id: clientId,
        entity_type: 'QUOTATION',
        operation: 'CREATE',
        payload: payload as unknown as Record<string, unknown>,
        device_id: deviceId,
        status: 'PENDING',
        created_at: now,
        retry_count: 0,
        last_error: null,
      });
    } catch (err) {
      // Rollback: delete the local row so we preserve the invariant
      // "every local_quotes row has a pending sync-queue entry". Swallow
      // the delete error (best-effort cleanup) and re-throw the original.
      await posDb.local_quotes.delete(clientId).catch(() => { /* best-effort */ });
      throw err;
    }

    return clientId;
  },

  /**
   * List local quotes scoped to a tenant + location. Returns a Quote-shape
   * envelope so callers can use this as a drop-in fallback for the server
   * listQuotes endpoint.
   */
  async listLocalQuotes(filters: ListLocalQuoteFilters): Promise<{
    data: LocalQuoteListItem[];
    meta: PaginationMeta;
  }> {
    const all = await decryptManyFromStorage(
      await posDb.local_quotes
        .where('[tenant_id+location_id]')
        .equals([filters.tenantId, filters.locationId])
        .toArray(),
    );

    const filtered = applyFilters(all, filters);

    const limit = filters.limit ?? 20;
    const page = filters.page ?? 1;
    const start = (page - 1) * limit;
    const sliced = filtered.slice(start, start + limit);

    return {
      data: sliced.map(toListItem),
      meta: {
        page,
        limit,
        total: filtered.length,
        totalPages: Math.max(1, Math.ceil(filtered.length / limit)),
      },
    };
  },

  /**
   * Fetch a single local quote by client_id.
   */
  async getLocalQuote(clientId: string): Promise<LocalQuoteListItem | null> {
    const row = await decryptFromStorage(await posDb.local_quotes.get(clientId));
    return row ? toListItem(row) : null;
  },

  /**
   * Remove a local quote — typically called after the server confirms sync.
   */
  async removeLocalQuote(clientId: string): Promise<void> {
    await posDb.local_quotes.delete(clientId);
  },

  /**
   * Mark a local quote as synced and attach the server-assigned id.
   *
   * G2.19: wired into sync-queue.service.processQueue — called on a SYNCED
   * or DUPLICATE result before the local row is removed. Acts as a safety
   * net: if the subsequent removeLocalQuote call fails, the row is at least
   * flagged is_synced=1 with a valid server_id so the QuotationListPage
   * merge dedupes correctly (see toListItem — `id` resolves to server_id
   * when present).
   *
   * Note on quote numbers: the server does NOT replace the client's offline
   * OFL quote_number — G2.17 preserves it as-is for audit continuity. This
   * method only attaches the server_id.
   */
  async markQuoteSynced(clientId: string, serverId: string): Promise<void> {
    await posDb.local_quotes.update(clientId, {
      is_synced: 1,
      server_id: serverId,
      updated_at: new Date().toISOString(),
    });
  },
};
